import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Card,
  Button,
  Chip,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { getActivePopProjects } from "../../Services/Brokerage";

/* ---------------- STEPS ---------------- */
const steps = [
  { label: "Validity Period", sub: "Financial year" },
  { label: "Calculation Rules", sub: "Define slabs" },
  { label: "Eligibility", sub: "Optional constraints" },
  { label: "CP Sign-Off", sub: "Configure window" },
  { label: "Exclusions", sub: "POP projects" },
  { label: "Review", sub: "Preview & save" },
];

export default function PopExclusions({ data, setData, onBack, onNext }) {
  const [loading, setLoading] = useState(true);
  const [popProjects, setPopProjects] = useState([]);

  /* ---------------- FETCH POP PROJECTS ---------------- */
  useEffect(() => {
    const fetchPopProjects = async () => {
      try {
        setLoading(true);
        const res = await getActivePopProjects();

        setPopProjects(res || []);

        // 🔐 Store exclusions in formData
        setData((prev) => ({
          ...prev,
          popExclusions: res || [],
        }));
      } catch (err) {
        console.error("Fetch POP exclusions error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPopProjects();
  }, [setData]);

  return (
    <Box sx={{ p: 1, backgroundColor: "#fff" }}>
    

{/* Back */}
<Typography
  variant="body2"
  sx={{
    mb: 3,
    cursor: "pointer",
    color: "text.secondary",
    fontSize: 14,
    fontWeight: 500,
  }}
  onClick={onBack}
>
  ← Back to AOPs
</Typography>

{/* Stepper */}
<Stepper
  activeStep={4}
  alternativeLabel
  sx={{
    mb: 6,
    "& .MuiStepLabel-label": {
      fontSize: "0.95rem",
      fontWeight: 500,
    },
    "& .MuiStepIcon-root": {
      fontSize: 32,
    },
    "& .Mui-active": {
      color: "#2563EB",
    },
    "& .Mui-completed": {
      color: "#2563EB",
    },
  }}
>
  {steps.map((step) => (
    <Step key={step.label}>
      <StepLabel>
        <Box textAlign="center">
          <Typography
            fontSize="0.95rem"
            fontWeight={600}
          >
            {step.label}
          </Typography>
          <Typography
            fontSize="0.75rem"
            color="text.secondary"
          >
            {step.sub}
          </Typography>
        </Box>
      </StepLabel>
    </Step>
  ))}
</Stepper>


      {/* Info */}
      <Card
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: "#EAF6FF",
          border: "1px solid #C7E4FF",
        }}
      >
        <Stack direction="row" spacing={1}>
          <InfoOutlinedIcon color="primary" />
          <Typography variant="body2">
            <b>AOP Achievement Logic</b>
            <br />
            AOP achievement excludes projects with active or upcoming POP.
          </Typography>
        </Stack>
      </Card>

      {/* Warning */}
      <Card
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: "#FFF6EB",
          border: "1px solid #FFD8A8",
        }}
      >
        <Stack direction="row" spacing={1}>
          <WarningAmberOutlinedIcon sx={{ color: "#F59E0B" }} />
          <Typography variant="body2">
            <b>AOP does not apply on POP projects</b>
            <br />
            The following projects are automatically excluded.
          </Typography>
        </Stack>
      </Card>

      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : popProjects.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No active or upcoming POP projects found. AOP applies to all projects.
        </Typography>
      ) : (
        popProjects.map((project) => (
          <Card
            key={project.projectId}
            sx={{
              p: 2,
              mb: 2,
              backgroundColor:
                project.status === "Active" ? "#F0FDF4" : "#FFF7ED",
              border:
                project.status === "Active"
                  ? "1px solid #BBF7D0"
                  : "1px solid #FED7AA",
            }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={2}>
                <ApartmentOutlinedIcon
                  color={project.status === "Active" ? "success" : "warning"}
                />
                <Box>
                  <Typography fontWeight={600}>
                    {project.projectName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {project.startDate} → {project.endDate}
                  </Typography>
                </Box>
              </Stack>

              <Chip
                label={project.status}
                size="small"
                color={project.status === "Active" ? "success" : "warning"}
              />
            </Stack>
          </Card>
        ))
      )}

      <Divider sx={{ my: 3 }} />

      {/* Footer */}
      <Stack direction="row" justifyContent="space-between">
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          variant="outlined"
          onClick={onBack}
        >
          Back
        </Button>
        <Button variant="contained" onClick={onNext}>
          Next →
        </Button>
      </Stack>
    </Box>
  );
}
